import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, 
  FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert, CircularProgress, InputAdornment,
  Card, CardContent, Divider, FormControlLabel, Switch,
  LinearProgress, Chip, IconButton
} from '@mui/material';
import {
  Upload, Smartphone, Download, CheckCircle, AlertCircle,
  FileText, Calendar, Hash, Tag, X, CloudUpload
} from 'lucide-react';
import api from '../services/api';

const AppUpdateUploadPage = () => {
  // Estados principais
  const [uploadData, setUploadData] = useState({
    version: '',
    build: '',
    changelog: '',
    force_update: false
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentVersionInfo, setCurrentVersionInfo] = useState(null);
  const [loadingVersionInfo, setLoadingVersionInfo] = useState(false);

  const fileInputRef = useRef(null);

  // Função para mostrar snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Carregar informações da versão atual
  const loadCurrentVersion = useCallback(async () => {
    setLoadingVersionInfo(true);
    try {
      const response = await api.get('/app-version-check');
      setCurrentVersionInfo(response.data);
    } catch (error) {
      console.error('Error loading current version:', error);
      showSnackbar('Error loading current version info', 'error');
    } finally {
      setLoadingVersionInfo(false);
    }
  }, []);

  // Carregar versão atual ao montar o componente
  React.useEffect(() => {
    loadCurrentVersion();
  }, [loadCurrentVersion]);

  // Handler para mudanças nos inputs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUploadData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handler para seleção de arquivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.apk')) {
        showSnackbar('Please select a valid APK file', 'warning');
        return;
      }
      
      if (file.size > 200 * 1024 * 1024) { // 200MB limit
        showSnackbar('File size must be less than 200MB', 'warning');
        return;
      }

      setSelectedFile(file);
      showSnackbar(`APK file selected: ${file.name}`, 'info');
    }
  };

  // Remover arquivo selecionado
  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validar dados antes do upload
  const validateData = () => {
    const { version, build, changelog } = uploadData;
    
    if (!version.trim()) {
      showSnackbar('Version is required', 'warning');
      return false;
    }
    
    if (!build.trim() || isNaN(parseInt(build))) {
      showSnackbar('Build number must be a valid number', 'warning');
      return false;
    }
    
    if (!changelog.trim()) {
      showSnackbar('Changelog is required', 'warning');
      return false;
    }
    
    if (!selectedFile) {
      showSnackbar('Please select an APK file', 'warning');
      return false;
    }

    // Verificar se é uma versão mais nova
    if (currentVersionInfo) {
      const currentBuild = parseInt(currentVersionInfo.latest_build);
      const newBuild = parseInt(build);
      
      if (newBuild <= currentBuild) {
        showSnackbar(`Build number must be greater than current build (${currentBuild})`, 'warning');
        return false;
      }
    }

    return true;
  };

  // Função de upload
  const handleUpload = async () => {
    if (!validateData()) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('apk_file', selectedFile);
      formData.append('version', uploadData.version.trim());
      formData.append('build', uploadData.build.trim());
      formData.append('changelog', uploadData.changelog.trim());
      formData.append('force_update', uploadData.force_update.toString());

      const response = await api.post('/app-upload-version', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        showSnackbar(`Version ${uploadData.version} uploaded successfully!`, 'success');
        
        // Reset form
        setUploadData({
          version: '',
          build: '',
          changelog: '',
          force_update: false
        });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Reload current version info
        await loadCurrentVersion();
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || 'Upload failed';
      showSnackbar(errorMessage, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box p={1} maxWidth={800} mx="auto">
      {/* Current Version Info */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Smartphone size={24} color="#1976d2" />
            <Typography variant="h6" fontWeight="bold">
              Current Version
            </Typography>
            {loadingVersionInfo && <CircularProgress size={20} />}
          </Box>
          
          {currentVersionInfo ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`v${currentVersionInfo.latest_version}`} 
                color="primary" 
                variant="outlined"
                icon={<Tag size={16} />}
              />
              <Chip 
                label={`Build ${currentVersionInfo.latest_build}`} 
                color="secondary" 
                variant="outlined"
                icon={<Hash size={16} />}
              />
              <Chip 
                label={currentVersionInfo.download_available ? 'APK Available' : 'No APK'} 
                color={currentVersionInfo.download_available ? 'success' : 'error'}
                variant="outlined"
                icon={currentVersionInfo.download_available ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              />
              {currentVersionInfo.file_size && (
                <Chip 
                  label={formatFileSize(currentVersionInfo.file_size)} 
                  variant="outlined"
                />
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">No version information available</Typography>
          )}
        </CardContent>
      </Card>

      {/* Upload Form */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUpload size={24} color="#1976d2" />
          Upload New Version
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Version and Build */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Version"
              name="version"
              value={uploadData.version}
              onChange={handleInputChange}
              placeholder="e.g., 3.0.5"
              fullWidth
              required
              disabled={uploading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Tag size={18} color="#666" />
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              label="Build Number"
              name="build"
              type="number"
              value={uploadData.build}
              onChange={handleInputChange}
              placeholder={currentVersionInfo ? `> ${currentVersionInfo.latest_build}` : "e.g., 35"}
              fullWidth
              required
              disabled={uploading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Hash size={18} color="#666" />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Changelog */}
          <TextField
            label="Changelog"
            name="changelog"
            value={uploadData.changelog}
            onChange={handleInputChange}
            placeholder="Describe what's new in this version..."
            multiline
            rows={4}
            fullWidth
            required
            disabled={uploading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                  <FileText size={18} color="#666" />
                </InputAdornment>
              )
            }}
          />

          {/* Force Update Toggle */}
          <FormControlLabel
            control={
              <Switch
                name="force_update"
                checked={uploadData.force_update}
                onChange={handleInputChange}
                disabled={uploading}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Force Update
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Users will be required to update immediately
                </Typography>
              </Box>
            }
          />

          <Divider />

          {/* File Upload */}
          <Box>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
              APK File
            </Typography>
            
            {!selectedFile ? (
              <Box
                sx={{
                  border: '2px dashed #ddd',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={48} color="#1976d2" style={{ marginBottom: 16 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Select APK File
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click to browse or drag and drop your APK file here
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Maximum file size: 200MB
                </Typography>
              </Box>
            ) : (
              <Card variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Smartphone size={24} color="#4caf50" />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(selectedFile.size)}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton 
                    onClick={handleRemoveFile} 
                    disabled={uploading}
                    sx={{ color: '#f44336' }}
                  >
                    <X size={20} />
                  </IconButton>
                </Box>
              </Card>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".apk"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Uploading...</Typography>
                <Typography variant="body2">{uploadProgress}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

          {/* Upload Button */}
          <Button
            onClick={handleUpload}
            disabled={uploading || !selectedFile || !uploadData.version || !uploadData.build || !uploadData.changelog}
            variant="contained"
            size="large"
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <Upload size={20} />}
            sx={{
              py: 1.5,
              background: 'linear-gradient(to right, #1976d2, #0d47a1)',
              '&:hover': { background: 'linear-gradient(to right, #1565c0, #003c8f)' },
              '&:disabled': { background: '#e0e0e0' }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload New Version'}
          </Button>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppUpdateUploadPage;